import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  OnDestroy
} from '@angular/core';
import type { GridSchemaItem, QuestionnaireTrackingModel, TrackingInfoResponseModel } from '@mds/api';
import {
  GridOverrides,
  parseAsUTC,
} from '@mds/common';

export interface FlattenedCampaignHistoryRow {
  event_id: number;
  created_at: string;
  feature_type: string;
  feature_id: string;
  action_type: string;
  user_id: string | null;
  email_type: string | null;
  campaign_rid: string | null;
  questionnaire_id: string[];
  email_status: string | null;
  to: string | null;
  subject: string | null;
  template_name: string | null;
  success: boolean | null;
  error_message: string | null;
  old_status: string | null;
  new_status: string | null;
  feature_id_link: string | null;
  site: string | null;
  site_link: string | null;
  sku: string | null;
  sku_link: string | null;
  user_id_link: string | null;
}
import { ColDef, GridApi, IGetRowsParams } from 'ag-grid-community';
import { MenuService } from '@services/menu.service';
import { GridModule, GridNoRowsComponent, LinkCellRendererComponent } from '@mds/ui';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, takeUntil } from 'rxjs/operators';
import { ReusableService } from '@services/reusable.service';
import { CommonModule, DatePipe } from '@angular/common';
import { Subject } from 'rxjs';
import { DataCollectionService } from '@pages/data-collection/data-collection.service';

@Component({
  selector: 'app-request-history',
  templateUrl: './request-history.component.html',
  styleUrls: ['./request-history.component.scss'],
  imports: [GridModule, CommonModule],
  standalone: true
})
export class RequestHistoryComponent implements OnInit, OnDestroy {
  @Input() rid: string;
  @Input() isCampaign = false;
  page = 1;
  campaignHistoryOverrides: GridOverrides;
  historyGridApi: GridApi;
  paginationPageSize = 20;
  loading = true;
  currentPage = 1;

  private destroyRef = inject(DestroyRef);
  private menuService = inject(MenuService);
  private reusableService = inject(ReusableService);
  private cdr = inject(ChangeDetectorRef);
  private datePipe = inject(DatePipe);
  private destroy$: Subject<void> = new Subject<void>();
  private dataCollectionService = inject(DataCollectionService);

  ngOnInit() {
    this.loading = false;
    this.createGridData();
    if (this.isCampaign) {
      this.menuService.reload$
        .pipe(
          filter((reload) => !!reload),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          if (this.historyGridApi) {
            this.historyGridApi.setGridOption('datasource', this.createDatasource());
          }
        });
    }
  }

  onGridReady(gridApi: GridApi) {
    this.historyGridApi = gridApi;
    if (this.historyGridApi) {
      this.historyGridApi.sizeColumnsToFit();
      this.historyGridApi.setGridOption('datasource', this.createDatasource());
    }
  }

  createDatasource() {
    return {
      getRows: (params: IGetRowsParams) => {
        if (!this.historyGridApi) return;
        this.historyGridApi?.setGridOption('loading', true);
        const newPage = this.historyGridApi.paginationGetCurrentPage() + 1;
        const newPageSize = this.historyGridApi.paginationGetPageSize();
        if (newPage !== this.currentPage || newPageSize !== this.paginationPageSize) {
          this.currentPage = newPage;
          this.paginationPageSize = newPageSize;
        }
        const page = Math.floor(params.startRow / this.paginationPageSize) + 1;
        this.dataCollectionService
          .getCampaignHistory(this.rid, this.isCampaign, this.currentPage, this.paginationPageSize)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(
            (res: TrackingInfoResponseModel) => {
              this.historyGridApi?.hideOverlay();
              this.historyGridApi?.setGridOption('loading', false);
              const rowData = this.flattenCampaignHistoryList(res.questionnaires);
              const columnDefs = this.getColumnDefs(res.gridSchema);
              this.historyGridApi?.setGridOption('columnDefs', columnDefs);
              if (res.total_count < 1) this.historyGridApi.showNoRowsOverlay();
              params.successCallback(rowData, res.total_count);
            },
            () => params.failCallback()
          );
      }
    };
  }

  private createGridData() {
    this.campaignHistoryOverrides = {
      rowModelType: 'infinite',
      pagination: true,
      paginationPageSize: this.paginationPageSize,
      paginationPageSizeSelector: [20, 50, 100],
      suppressRowClickSelection: true,
      defaultColDef: {
        flex: 1,
        minWidth: 100,
        sortable: true,
        filter: true,
        resizable: true
      },
      noRowsOverlayComponent: GridNoRowsComponent,
      noRowsOverlayComponentParams: {
        title: 'No data to show the table',
        description: '',
        onClick: '',
        buttonLabel: ''
      }
    } as GridOverrides;
    this.cdr.detectChanges();
  }

  private linkRendererParams = (linkKey: string) => (params) =>
    params.data?.[linkKey] ? { urlPrefix: params.data[linkKey] } : null;

  private getColumnDefs(schema: GridSchemaItem[]): ColDef[] {
    if (schema?.length) {
      this.reusableService.pradicateBy(schema, 'sortlevel');
      const header: ColDef[] = [];
      const excludeTooltipFields = ['created_at', 'feature_id', 'site', 'sku'];
      schema.forEach((element: GridSchemaItem) => {
        const column: ColDef = {
          field: element.key,
          headerName: element.value,
          hide: element.hidden || (!this.isCampaign && element.key === 'feature_id'),
          minWidth: 100,
          ...(!excludeTooltipFields.includes(element.key) && {
            tooltipField: element.key
          })
        };
        if (column.field === 'created_at') {
          column.minWidth = 160;
          column.valueFormatter = (params) => this.datePipe.transform(parseAsUTC(params.value), 'dd.MM.yyyy') ?? '';
          column.tooltipValueGetter = (params) => this.datePipe.transform(parseAsUTC(params.value), 'dd.MM.yyyy') ?? '';
        }
        const linkFieldMap: Record<string, string> = {
          feature_id: 'feature_id_link',
          site: 'site_link',
          sku: 'sku_link',
          user_id: 'user_id_link'
        };
        const linkKey = linkFieldMap[column.field];
        if (linkKey) {
          column.cellRenderer = LinkCellRendererComponent;
          column.cellRendererParams = this.linkRendererParams(linkKey);
        }
        header.push(column);
      });
      return header;
    }
  }

  private flattenCampaignHistory(item): FlattenedCampaignHistoryRow {
    return {
      event_id: item.id,
      created_at: item.created_at,
      feature_type: item.feature_type,
      feature_id: item.feature_id,
      action_type: item.action_type,
      user_id: item.user_id ?? null,
      email_type: item.context?.email_type ?? null,
      campaign_rid: item.context?.campaign_rid ?? null,
      to: item.data?.to ?? null,
      subject: item.data?.subject ?? null,
      template_name: item.data?.template_name ?? null,
      success: item.data?.success ?? null,
      error_message: item.data?.error_message ?? null,
      old_status: item.data?.old_status ?? null,
      questionnaire_id: item.data?.questionnaire_id ?? [],
      new_status: item.data?.new_status ?? null,
      email_status: item.data?.email_status ?? null,
      feature_id_link: item.feature_id_link?.url ?? null,
      site: item?.site ?? null,
      site_link: item?.site_link?.url ?? null,
      sku: item?.sku ?? null,
      sku_link: item?.sku_link?.url ?? null,
      user_id_link: item?.user_id_link?.url ?? null
    };
  }

  private flattenCampaignHistoryList(items: QuestionnaireTrackingModel[]): FlattenedCampaignHistoryRow[] {
    return items.flatMap((questionnaire) =>
      (questionnaire.events ?? []).map((item) => this.flattenCampaignHistory(item))
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
